import { AxiosResponse } from 'axios';
import { instance as axiosInstance } from 'core/util';
import { getDefaultHeaders } from 'core/util/other';
import { Dictionary, isUnsupportedQuery, themeObject } from 'lib';

export const useQueryProps = {
    retry: false,
    refetchOnWindowFocus: false,
};

export const getQueryData = async (
    url: string,
    token: string,
    params?: any
): Promise<any> => {
    const headers = getDefaultHeaders(token);
    const response: AxiosResponse<any> = await axiosInstance().get(url, {
        headers,
        params,
    });

    return response.data;
};

export const graphSettings = {
    nodeId: 'key',
    backgroundColor: 'white',
};

export const linkColors: any = {
    HAS: 'green',
    RENDERED: 'blue',
    default: themeObject.palette.grayScale[400],
};

const getLinkColor = (link: any): string => {
    const currentLinklabel: string = link.label;
    if (currentLinklabel === 'HAS' || currentLinklabel === 'RENDERED') {
        return linkColors[currentLinklabel];
    } else {
        return linkColors.default;
    }
};

export const linkSettings = {
    linkLabel: (link: any): string => `<span>${link.label}</span>`,
    linkDirectionalArrowLength: 3.5,
    linkDirectionalArrowRelPos: 1,
    linkWidth: 0.5,
    linkColor: (link: any): string => getLinkColor(link),
    // linkCurvature = 0 is a straight line, so a link will either be rendered as a line or a curve.
    linkCurvature: (link: any): number =>
        link.linknum === 0 ? 0 : 0.75 / link.linknum,
};

export const getNodeColor = (
    node: any,
    nodeColors: Record<string, string>
): string => {
    const currentNodeType = node?.sdo?.header?.dictionary ?? '';

    // returns the default color in case currentNodeType is empty. Otherwise,
    // we should always pick the correct color from the normalized dictionary color list.
    return nodeColors[currentNodeType] ?? themeObject.palette.primary[400];
};

export const nodeSettings = {
    nodeVal: 2,
    nodeLabel: (node: any): string =>
        `<div>
        <span><b>Name:</b>&nbsp;${node?.sdo?.header?.dictionary}</span>
        <span> <b>ID:</b>&nbsp;${node.key}</span>
    </div>`,
    fixed: true,
};

const getPrimaryAttributesForCurrentNode = (
    nodeName: string,
    dictionaries: Dictionary[]
): string[] => {
    const dictionaryDetails = dictionaries.find(
        (dictionary: Dictionary) => dictionary.name === nodeName
    );
    const dictionaryIndexes = dictionaryDetails?.indexes ?? [];

    return (
        dictionaryIndexes.find((index: any) => index.type === 'PRIMARY')
            ?.attributes ?? []
    );
};

const getFormattedAttributes = (
    node: any,
    dictionaries: Dictionary[]
): string => {
    const primaryAttributesForCurrentNode: string[] =
        getPrimaryAttributesForCurrentNode(
            node?.sdo?.header?.dictionary ?? '',
            dictionaries
        );

    if (!primaryAttributesForCurrentNode.length)
        return `<span> <b>ID:</b>&nbsp;${node.key}</span>`;

    const data: any = node?.sdo?.data;
    let primaryKeyAttributesWithLabelIndicator = {};

    // We want to mark any primary key attributes with an asterisk for visual representation if data exists.
    if (data) {
        for (const attribute of primaryAttributesForCurrentNode) {
            for (const [key, value] of Object.entries(data)) {
                if (attribute === key) {
                    primaryKeyAttributesWithLabelIndicator = {
                        ...primaryKeyAttributesWithLabelIndicator,
                        [`${key}*`]: value,
                    };
                }
            }
        }
    }

    let attributeValue = '';
    for (const [key, value] of Object.entries(
        primaryKeyAttributesWithLabelIndicator
    )) {
        attributeValue += `<span> <b>${key}:</b>&nbsp;${value}</span>`;
    }

    return attributeValue;
};

export const getNodeSettings = (dictionaries: Dictionary[]): any => {
    return {
        nodeVal: 2,
        nodeLabel: (node: any): string =>
            `<div>
        <span><b>Name:</b>&nbsp;${node?.sdo?.header?.dictionary}</span>
        ${getFormattedAttributes(node, dictionaries)}
    </div>`,
    };
};

export const styles = {
    chainImg: {
        display: 'inline-block',
        width: '292px',
        margin: '0 auto',
        height: 'auto',
    },
    childrenContainerHeight: {
        height: 'calc(100% - 97px)',
    },
    box: {
        height: '100%',
    },
    selectChainStack: {
        backgroundColor: 'white',
        height: '100%',
        borderRadius: '8px',
        border: `1px solid ${themeObject.palette.grayScale[300]}`,
        display: 'flex',
        justifyContent: 'center',
        mb: 2,
    },
    selectChainBox: {
        textAlign: 'center',
    },
    selectChainText: {
        textAlign: 'center',
        mt: 2,
        color: themeObject.palette.grayScale[700],
    },
    filterHeader: {
        display: 'flex',
        my: 2,
    },
    filterTitleContainer: {
        display: 'flex',
        alignItems: 'center',
        mr: 2,
    },
    filterTitle: {
        color: themeObject.palette.primary[700],
        fontWeight: '500',
        fontFamily: 'Raleway',
        fontSize: '20px',
        lineHeight: '23.48px',
        ml: 0.5,
    },
    filterButtonText: {
        color: themeObject.palette.primary[700],
        fontFamily: 'Red Hat Text',
        fontWeight: 700,
        fontSize: '14px',
        lineHeight: '22px',
    },
    arrowDownIcon: {
        color: themeObject.palette.primary.dark,
        fontSize: '18px',
    },
    filterButton: {
        p: '4px 5px',
    },
    filterIcon: {
        width: '20px',
        height: '20px',
    },
    collapseContainerStyles: {
        height: 'calc(100% - 29px) !important',
        '& .MuiCollapse-wrapper': {
            height: '100%',
            '& .MuiCollapse-wrapperInner': {
                height: '100%',
            },
        },
    },
    collapseInnerContainerStyles: {
        height: '100%',
    },
};

export const lifegraphExplorerEmptyStateStyles = {
    mainContainer: {
        backgroundColor: 'white',
        height: '100%',
        borderRadius: '8px',
        border: `1px solid ${themeObject.palette.grayScale[300]}`,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'auto',
    },
    headerTextContainer: {
        mt: 4,
    },
    subheaderTextContainer: {
        mt: 2,
    },
    errorImage: {
        height: '162.6px',
        width: '286.53px',
    },
    paddingStyles: {
        pt: '120px',
        pb: '20px',
    },
};

export const gridSize = 12;

export const isUnsupportedLGQuery = (query: string): boolean => {
    const unsupportedKeywords = ['FROM', 'GRAPH2 UNIFIED'];
    return isUnsupportedQuery(query, unsupportedKeywords);
};
